/**
 * Streaming read-aloud playback (round 3, revised after a real-browser
 * failure). Two steps: `POST /voice/tts/prepare` returns the clip's URL (auth,
 * budget, speakable rewrite, cache key); `GET /voice/tts/:projectId/:file`
 * streams the MP3 from the provider's first chunk (or serves the cache).
 *
 * Two ways to play that URL:
 * - **MediaSource** where the browser accepts `audio/mpeg` (Chrome, Edge,
 *   Firefox): fetch the URL, append chunks to a SourceBuffer, play after the
 *   first append — ≈0.7 s after the press on a cache miss. A watchdog falls
 *   back to the plain URL if the element has not started within
 *   `MSE_START_TIMEOUT_MS`, so a browser whose MSE claims support it cannot
 *   deliver never leaves the button spinning.
 * - **Plain `<audio src>`** everywhere else (iOS Safari has no MediaSource):
 *   every browser plays a chunked `audio/mpeg` URL progressively (how radio
 *   streams work); Chrome needs ≈3.5 s of probe data on this path, which is
 *   why MSE is preferred where it exists.
 *
 * The element is primed inside the click (`primeAudio`) because Safari only
 * lets an element play later if a user gesture called `play()` on it first.
 *
 * Pure of React so fakes (`Audio`, `fetch`, `MediaSource`) can drive it in tests.
 */

/** Same-origin API path (mirrors `toApiUrl` without pulling the RPC client into tests). */
const apiUrl = (path: string) => (path.startsWith('/') ? path : `/${path}`)

export const TTS_MIME = 'audio/mpeg'
/** MSE: give up on the first `playing` after this and switch to the plain URL. */
export const MSE_START_TIMEOUT_MS = 4_000

export interface PreparedTts {
  url: string
  cached: boolean
}

export class TtsRequestError extends Error {
  constructor(
    readonly status: number,
    readonly reason: 'failed' | 'credits_exhausted'
  ) {
    super(`tts prepare ${status}`)
    this.name = 'TtsRequestError'
  }
}

/**
 * Unlock the element inside the user's click: iOS/Safari only let a media
 * element play programmatically later if `play()` was first called from a
 * user gesture on that element. There is no source yet, so the promise
 * rejects — that is fine, the restriction is lifted all the same.
 */
export function primeAudio(audio: HTMLAudioElement): void {
  try {
    const p = audio.play()
    if (p && typeof p.catch === 'function') p.catch(() => {})
  } catch {
    // some engines throw synchronously without a source
  }
}

/** Step one: auth, budget, rewrite, cache key — everything but the audio. */
export async function prepareTts(
  input: { projectId: string; text: string; voice: 'female' | 'male' },
  signal: AbortSignal,
  doFetch: typeof fetch = fetch
): Promise<PreparedTts> {
  const res = await doFetch(apiUrl('/voice/tts/prepare'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
    signal,
  })
  if (res.status === 402) throw new TtsRequestError(402, 'credits_exhausted')
  if (!res.ok) throw new TtsRequestError(res.status, 'failed')
  const body = (await res.json()) as { url?: string; cached?: boolean }
  if (!body.url) throw new TtsRequestError(500, 'failed')
  return { url: apiUrl(body.url), cached: Boolean(body.cached) }
}

/** Detach the element from its source: the browser cancels the request, the route aborts the provider. */
export function releaseAudio(audio: HTMLAudioElement): void {
  audio.pause()
  const src = audio.src
  audio.removeAttribute('src')
  try {
    audio.load()
  } catch {
    // detached element in a test double
  }
  if (src.startsWith('blob:')) URL.revokeObjectURL(src)
}

// ── MediaSource path ────────────────────────────────────────────────────

export interface MediaSourceLike {
  readyState: string
  addSourceBuffer(type: string): SourceBufferLike
  endOfStream(): void
  addEventListener(type: 'sourceopen', listener: () => void, options?: { once: boolean }): void
}
export interface SourceBufferLike {
  updating: boolean
  appendBuffer(data: ArrayBuffer | ArrayBufferView): void
  addEventListener(
    type: 'updateend' | 'error',
    listener: () => void,
    options?: { once: boolean }
  ): void
  removeEventListener(type: 'updateend' | 'error', listener: () => void): void
}
export interface MediaSourceCtor {
  new (): MediaSourceLike
  isTypeSupported(type: string): boolean
}

/** The `MediaSource` constructor to use, or null when the plain URL path is the one. */
export function streamingMediaSource(
  win: { MediaSource?: unknown } = globalThis as unknown as { MediaSource?: unknown }
): MediaSourceCtor | null {
  const ctor = win.MediaSource as MediaSourceCtor | undefined
  try {
    return ctor && typeof ctor.isTypeSupported === 'function' && ctor.isTypeSupported(TTS_MIME)
      ? ctor
      : null
  } catch {
    return null
  }
}

function waitUpdateEnd(sb: SourceBufferLike): Promise<void> {
  if (!sb.updating) return Promise.resolve()
  return new Promise<void>((resolve, reject) => {
    const done = () => {
      sb.removeEventListener('updateend', done)
      sb.removeEventListener('error', fail)
      resolve()
    }
    const fail = () => {
      sb.removeEventListener('updateend', done)
      sb.removeEventListener('error', fail)
      reject(new Error('SourceBuffer error'))
    }
    sb.addEventListener('updateend', done)
    sb.addEventListener('error', fail)
  })
}

export interface PlayOptions {
  audio: HTMLAudioElement
  url: string
  /** Aborted by stop(): reading stops, nothing more is appended. */
  signal: AbortSignal
  mediaSource: MediaSourceCtor | null
  /** Fires once the element is playing — the caller flips to `playing`. */
  onPlaying: () => void
  doFetch?: typeof fetch
  createObjectURL?: (obj: MediaSource) => string
  startTimeoutMs?: number
}

/**
 * Play the prepared URL. Resolves when playback has started (`onPlaying`
 * fired) and the feed is committed to finishing on its own; rejects on abort
 * or when neither path could start.
 */
export async function playTtsUrl(options: PlayOptions): Promise<void> {
  const { audio, url, signal } = options
  if (!options.mediaSource) {
    await playPlainUrl(audio, url, signal)
    options.onPlaying()
    return
  }
  try {
    await playViaMediaSource(options)
  } catch (err) {
    if (signal.aborted) throw err
    // MSE could not start (unsupported bytestream, stuck element) — the plain
    // URL is the one every browser plays; the route serves the same clip.
    console.warn('[tts] MediaSource playback did not start — falling back to the plain URL', err)
    releaseAudio(audio)
    await playPlainUrl(audio, url, signal)
    options.onPlaying()
  }
}

async function playPlainUrl(audio: HTMLAudioElement, url: string, signal: AbortSignal) {
  if (signal.aborted) throw signal.reason ?? new DOMException('aborted', 'AbortError')
  audio.preload = 'auto'
  audio.src = url
  await audio.play()
}

async function playViaMediaSource(options: PlayOptions): Promise<void> {
  const { audio, url, signal } = options
  const MediaSourceCtor = options.mediaSource!
  const doFetch = options.doFetch ?? fetch
  const createObjectURL = options.createObjectURL ?? ((o: MediaSource) => URL.createObjectURL(o))
  const startTimeoutMs = options.startTimeoutMs ?? MSE_START_TIMEOUT_MS

  const res = await doFetch(url, { credentials: 'include', signal })
  if (!res.ok || !res.body) throw new Error(`tts stream ${res.status}`)

  const ms = new MediaSourceCtor()
  const opened = new Promise<void>((resolve) =>
    ms.addEventListener('sourceopen', () => resolve(), { once: true })
  )
  audio.src = createObjectURL(ms as unknown as MediaSource)
  await opened
  if (signal.aborted) throw signal.reason ?? new DOMException('aborted', 'AbortError')
  const sb = ms.addSourceBuffer(TTS_MIME)

  // Started = the element reports `playing` (not just a resolved play()).
  const started = new Promise<void>((resolve, reject) => {
    const onPlaying = () => {
      audio.removeEventListener('playing', onPlaying)
      resolve()
    }
    audio.addEventListener('playing', onPlaying)
    setTimeout(() => {
      audio.removeEventListener('playing', onPlaying)
      reject(new Error(`no playback after ${startTimeoutMs} ms`))
    }, startTimeoutMs)
  })

  const reader = res.body.getReader()
  const onAbort = () => void reader.cancel().catch(() => {})
  signal.addEventListener('abort', onAbort, { once: true })
  let playRequested = false
  // The feed keeps running after we resolve; its failures end playback via the element.
  const feed = (async () => {
    try {
      for (;;) {
        const { done, value } = await reader.read()
        if (signal.aborted) throw signal.reason ?? new DOMException('aborted', 'AbortError')
        if (done) break
        await waitUpdateEnd(sb)
        sb.appendBuffer(value)
        if (!playRequested) {
          playRequested = true
          await waitUpdateEnd(sb)
          void audio.play().catch(() => {})
        }
      }
      await waitUpdateEnd(sb)
      if (ms.readyState === 'open') ms.endOfStream()
    } finally {
      signal.removeEventListener('abort', onAbort)
    }
  })()
  feed.catch(() => {})

  try {
    await Promise.race([started, feed.then(() => started)])
  } catch (err) {
    void reader.cancel().catch(() => {})
    throw err
  }
  options.onPlaying()
}
